import JSONbig from 'json-bigint';
import BigNumber from 'bignumber.js';

export const JSON_MAX_BYTES = 10 * 1024 * 1024;
export const JSON_MAX_OUTPUT = 8 * 1024 * 1024;
export const JSON_MAX_DEPTH = 100;

const jsonParser = JSONbig({
  alwaysParseAsBig: true,
  constructorAction: 'preserve',
  protoAction: 'preserve',
  strict: true,
});

function abortIfNeeded(signal) {
  if (signal?.aborted) throw new DOMException('Conversion canceled.', 'AbortError');
}

function enforceDepth(text) {
  let depth = 0;
  let inString = false;
  let escaped = false;
  for (const char of text) {
    if (inString) {
      if (escaped) escaped = false;
      else if (char === '\\') escaped = true;
      else if (char === '"') inString = false;
      continue;
    }
    if (char === '"') { inString = true; continue; }
    if (char === '{' || char === '[') {
      depth += 1;
      if (depth > JSON_MAX_DEPTH) throw new Error(`JSON nesting exceeds ${JSON_MAX_DEPTH} levels.`);
    } else if (char === '}' || char === ']') {
      depth = Math.max(0, depth - 1);
    }
  }
}

function isBigNumber(value) {
  return BigNumber.isBigNumber(value)
    && typeof value.abs === 'function'
    && typeof value.isInteger === 'function'
    && typeof value.toString === 'function';
}

function isObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value) && !isBigNumber(value);
}

function isPrimitive(value) {
  return value === null || typeof value === 'string' || typeof value === 'boolean' || typeof value === 'number' || isBigNumber(value);
}

function isLargeInteger(value) {
  return isBigNumber(value) && value.isInteger() && value.abs().isGreaterThan('9007199254740991');
}

function normalizeIndent(indent) {
  if (indent === 'tab') return '\t';
  if (indent === undefined || indent === null || indent === '') return 2;
  const value = Number(indent);
  if (!Number.isInteger(value) || value < 0 || value > 10) throw new Error('JSON indentation must be 0 to 10 spaces or tabs.');
  return value;
}

function exactJson(value) {
  return jsonParser.stringify(value, null, 0);
}

function parseJson(text) {
  try {
    return jsonParser.parse(text);
  } catch (error) {
    if (error instanceof Error) throw error;
    throw new SyntaxError(error?.message || 'Invalid JSON.');
  }
}

function formatPrimitive(value) {
  if (value === null) return 'null';
  const raw = typeof value === 'string' ? value : exactJson(value);
  const fence = raw.includes('`') ? '``' : '`';
  return `${fence}${raw.replaceAll(fence, `${fence} `)}${fence}`;
}

function formatNested(value) {
  const raw = exactJson(value);
  const fence = raw.includes('`') ? '``' : '`';
  return `${fence}${raw.replaceAll(fence, `${fence} `)}${fence}`;
}

function tableCell(value) {
  const rendered = isPrimitive(value) ? formatPrimitive(value) : formatNested(value);
  return rendered.replace(/\\/g, '\\\\').replace(/\|/g, '\\|').replace(/\r\n?|\n/g, '<br>');
}

function objectKeys(records) {
  const keys = [];
  const seen = new Set();
  records.forEach((record) => {
    Object.keys(record).forEach((key) => {
      if (!seen.has(key)) { seen.add(key); keys.push(key); }
    });
  });
  return keys;
}

function jsonTable(records) {
  const keys = objectKeys(records);
  if (!keys.length) return `| Value |\n| --- |\n${records.map(() => '| `{}` |').join('\n')}`;
  const header = keys.map((key) => key.replace(/\\/g, '\\\\').replace(/\|/g, '\\|').replace(/\r\n?|\n/g, '<br>'));
  return [
    `| ${header.join(' | ')} |`,
    `| ${keys.map(() => '---').join(' | ')} |`,
    ...records.map((record) => `| ${keys.map((key) => Object.prototype.hasOwnProperty.call(record, key) ? tableCell(record[key]) : '').join(' | ')} |`),
  ].join('\n');
}

function objectOutline(value, signal, indent) {
  abortIfNeeded(signal);
  return Object.keys(value).map((key) => {
    abortIfNeeded(signal);
    const label = key.replace(/([\\*_[\]`])/g, '\\$1');
    const child = value[key];
    if (isPrimitive(child)) return `- **${label}:** ${formatPrimitive(child)}`;
    const raw = jsonParser.stringify(child, null, indent);
    const fence = raw.includes('```') ? '````' : '```';
    return `- **${label}:**\n  ${fence}json\n${raw.split('\n').map((line) => `  ${line}`).join('\n')}\n  ${fence}`;
  }).join('\n');
}

function renderJson(value, signal, mode = 'auto', indent = 2) {
  abortIfNeeded(signal);
  if (!['auto', 'table', 'structure'].includes(mode)) throw new Error('JSON output mode must be auto, table, or structure.');
  if (mode === 'table' && (!Array.isArray(value) || !value.length || !value.every(isObject))) {
    throw new Error('Table view requires a non-empty JSON array of objects.');
  }
  if (Array.isArray(value)) {
    if (mode !== 'structure' && value.length && value.every(isObject)) return jsonTable(value);
    if (mode !== 'table' && value.every(isPrimitive)) return ['| Value |', '| --- |', ...value.map((item) => `| ${tableCell(item)} |`)].join('\n');
    const fence = '```';
    return `${fence}json\n${jsonParser.stringify(value, null, indent)}\n${fence}`;
  }
  if (isObject(value) && mode !== 'structure') {
    if (!Object.keys(value).length) return '```json\n{}\n```';
    return `# JSON object\n\n${objectOutline(value, signal, indent)}`;
  }
  const fence = '```';
  return `${fence}json\n${jsonParser.stringify(value, null, indent)}\n${fence}`;
}

export function convertJsonToMarkdown(source, { signal, mode = 'auto', indent = 2, preserveBigInt = true } = {}) {
  const text = String(source ?? '').replace(/^\uFEFF/, '');
  if (!text.trim()) throw new Error('Paste JSON or choose a JSON file first.');
  if (new TextEncoder().encode(text).byteLength > JSON_MAX_BYTES) throw new Error('The JSON source is larger than 10 MB.');
  abortIfNeeded(signal);
  enforceDepth(text);
  const spacing = normalizeIndent(indent);
  const value = parseJson(text);
  const markdown = renderJson(value, signal, mode, spacing).trim();
  if (new TextEncoder().encode(markdown).byteLength > JSON_MAX_OUTPUT) throw new Error('The generated Markdown is larger than 8 MB.');
  const warnings = [];
  let hasLargeInteger = false;
  const walk = (current) => {
    if (isLargeInteger(current)) { hasLargeInteger = true; return; }
    if (Array.isArray(current)) current.forEach(walk);
    else if (isObject(current)) Object.values(current).forEach(walk);
  };
  walk(value);
  if (hasLargeInteger && !preserveBigInt) warnings.push('Large integer text was preserved because lossless parsing prevents silent precision loss.');
  if (hasLargeInteger && preserveBigInt) warnings.push('Large integer text was preserved exactly by the lossless parser.');
  if (Array.isArray(value) && value.length && value.every(isObject)) {
    const keys = objectKeys(value);
    if (keys.some((key) => value.some((record) => !Object.prototype.hasOwnProperty.call(record, key)))) {
      warnings.push('Object rows have different fields; the table uses the union of all fields and leaves missing cells blank.');
    }
  }
  return { markdown, warnings, assets: [], value };
}

export function validateJsonText(source) {
  try {
    const text = String(source ?? '');
    if (!text.trim()) return 'Paste JSON or choose a JSON file first.';
    if (new TextEncoder().encode(text).byteLength > JSON_MAX_BYTES) return 'The JSON source is larger than 10 MB.';
    enforceDepth(text);
    parseJson(text);
    return '';
  } catch (error) {
    return error instanceof Error ? error.message : 'Invalid JSON.';
  }
}

/** Cheap UI validation; lossless parsing stays inside the cancellable Worker. */
export function validateJsonInput(source, { mode = 'auto', indent = 2 } = {}) {
  const text = String(source ?? '');
  if (!text.trim()) return 'Paste JSON or choose a JSON file first.';
  if (new TextEncoder().encode(text).byteLength > JSON_MAX_BYTES) return 'The JSON source is larger than 10 MB.';
  if (!['auto', 'table', 'structure'].includes(mode)) return 'JSON output mode must be auto, table, or structure.';
  try { normalizeIndent(indent); } catch (error) { return error instanceof Error ? error.message : 'Invalid JSON indentation.'; }
  return '';
}
